'use client';

import { DataResponse } from '@/common/Response';
import { env } from '@/config/app';
import { AuthContext, AuthContextValue } from '@/context/AuthContext';
import { IBrandResponse } from '@/data/brand/types';
import { IChangeProfileVariables } from '@/domain/user/entities';
import { userKeys } from '@/query/keys';
import authService from '@/services/AuthService';
import brandService from '@/services/BrandService';
import userService from '@/services/UserService';
import { useBrandStore } from '@/stores/brandStore';
import { useProfileStore } from '@/stores/profileStore';
import { getAndRefreshToken } from '@/utils/api';
import { clearCookieUserAuthenticated, setCookie } from '@/utils/cookie.util';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';

export const useAuthContext = () => React.useContext(AuthContext);

interface AppProviderProps {
  children: React.ReactNode;
}
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  const { setValProfile, setValAuth, valAuth } = useProfileStore();
  const { setBrand } = useBrandStore();

  const [isAuthLoaded, setAuthLoaded] = React.useState<boolean>(false);

  const initialAuthContext: AuthContextValue = {
    isAuthLoaded,
  };

  React.useEffect(() => {
    const setBrandCookie = async () => {
      try {
        const domain: string = window.location.hostname;
        const checkDomain: string = domain === 'localhost' ? domain + '.com' : domain;

        const dataResponse: DataResponse<IBrandResponse> = await brandService.getByDomain(checkDomain);

        const {
          data: { id },
        } = dataResponse;
        setCookie(env.BrandX, id, 31557600); // 1 year

        setBrand(dataResponse?.data ?? null);

        const token: string = await getAndRefreshToken();
        if (!token) throw new Error('Token not found');

        const responseAuthentication = await authService.authenticate(token);

        const { data: dataAuthentication } = responseAuthentication;
        if (dataAuthentication) {
          setValAuth(dataAuthentication);
        }

        const responseProfileMe: DataResponse<IChangeProfileVariables> = await userService.getProfileMe();

        if (responseProfileMe?.data) {
          queryClient.setQueryData(userKeys.me(), responseProfileMe);
          setValProfile(responseProfileMe?.data);
        }
      } catch (_error) {
        clearCookieUserAuthenticated();
      }

      setAuthLoaded(true);
    };
    setBrandCookie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (valAuth) return;

    // clear query client data
    queryClient.clear();
  }, [valAuth]);

  return <AuthContext.Provider value={initialAuthContext}>{children}</AuthContext.Provider>;
};
